import React from 'react';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { Formik, FieldArray } from 'formik';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import DatePickerField from '../../form/DatePickerField';
import SelectField from '../../form/SelectField';
import LabelField from '../../form/LabelField';
import ScrollToFirstError from '../../route/ScrollToFirstError';

const ProfileFormStep2 = ({ schema, onSubmit, onBack, initialValues, dataTypes, isLoading, isEdit }) => {
    const { questionPhrasing } = dataTypes;
    const enabledFields = dataTypes.enabledFields || [];
    const { t } = useTranslation();

    return (
        <Formik
            validationSchema={schema}
            onSubmit={onSubmit}
            initialValues={initialValues}
            enableReinitialize={true}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {({ handleSubmit, handleChange, values, errors }) => {
                const members = values.hhMembers || [];

                return (
                    <Col md={6} sm={12} className="mx-auto">
                        <Form noValidate onSubmit={handleSubmit} className="card shadow-sm rounded p-4">
                            <h4 className="text-center">{t('intake.step2Heading')}</h4>
                            <p className="text-center text-muted mb-4">
                                {t('intake.step2Of3')}
                            </p>

                            <FieldArray
                                name="hhMembers"
                                render={(arrayHelpers) => (
                                    <>
                                        {members.length === 0 ? (
                                            <Card className="mb-4">
                                                <Card.Body className="text-center py-4">
                                                    <p className="mb-1 text-muted">
                                                        {t('intake.noHouseholdMembers')}
                                                    </p>
                                                    <p className="mb-4">
                                                        {t('intake.noHouseholdMembersDescription')}
                                                    </p>
                                                    <Button
                                                        type="button"
                                                        variant="outline-secondary"
                                                        className="w-100"
                                                        onClick={() => {
                                                            arrayHelpers.push({
                                                                firstName: '',
                                                                lastName: '',
                                                                dob: '',
                                                                hhmEstimatedDOB: false,
                                                                hhmGender: '',
                                                                hhmRelationship: '',
                                                            });
                                                        }}
                                                    >
                                                        {t('intake.addMember')}
                                                    </Button>
                                                </Card.Body>
                                            </Card>
                                        ) : (
                                            <>
                                                {members.map((member, index) => {
                                                    return (
                                                        <Card className="mb-3" key={`member-${index}`}>
                                                            <Card.Header className="text-center font-weight-bold">
                                                                {t('intake.householdMember')}{' '}
                                                                {index + 1}
                                                            </Card.Header>
                                                            <Card.Body>
                                                                <Form.Group controlId={`hhMembers.${index}.firstName`}>
                                                                    <LabelField
                                                                        label={questionPhrasing.firstName || t('personal.firstName')}
                                                                        isRequired={true}
                                                                    />
                                                                    <Form.Control
                                                                        type="text"
                                                                        name={`hhMembers.${index}.firstName`}
                                                                        placeholder="First Name"
                                                                        onChange={handleChange}
                                                                        value={member.firstName || ''}
                                                                        isInvalid={
                                                                            errors.hhMembers &&
                                                                            errors.hhMembers[index] &&
                                                                            !!errors.hhMembers[index].firstName
                                                                        }
                                                                    />
                                                                    <Form.Control.Feedback type="invalid">
                                                                        {errors.hhMembers &&
                                                                            errors.hhMembers[index] &&
                                                                            errors.hhMembers[index].firstName}
                                                                    </Form.Control.Feedback>
                                                                </Form.Group>

                                                                <Form.Group controlId={`hhMembers.${index}.lastName`}>
                                                                    <LabelField
                                                                        label={questionPhrasing.lastName || t('personal.lastName')}
                                                                        isRequired={true}
                                                                    />
                                                                    <Form.Control
                                                                        type="text"
                                                                        name={`hhMembers.${index}.lastName`}
                                                                        placeholder="Last Name"
                                                                        onChange={handleChange}
                                                                        value={member.lastName || ''}
                                                                        isInvalid={
                                                                            errors.hhMembers &&
                                                                            errors.hhMembers[index] &&
                                                                            !!errors.hhMembers[index].lastName
                                                                        }
                                                                    />
                                                                    <Form.Control.Feedback type="invalid">
                                                                        {errors.hhMembers &&
                                                                            errors.hhMembers[index] &&
                                                                            errors.hhMembers[index].lastName}
                                                                    </Form.Control.Feedback>
                                                                </Form.Group>

                                                                {enabledFields.includes('dateOfBirth') && (
                                                                    <Form.Group controlId={`hhMembers.${index}.dob`}>
                                                                        <LabelField
                                                                            label={questionPhrasing.dob || t('personal.dob')}
                                                                            isRequired={false}
                                                                        />
                                                                        <DatePickerField
                                                                            className={
                                                                                errors.hhMembers &&
                                                                                errors.hhMembers[index] &&
                                                                                !!errors.hhMembers[index].dob
                                                                                    ? 'form-control is-invalid'
                                                                                    : 'form-control'
                                                                            }
                                                                            name={`hhMembers.${index}.dob`}
                                                                            popperClassName="d-none"
                                                                            placeholderText={dataTypes.dateFormat.toUpperCase()}
                                                                            dateFormat={dataTypes.dateFormat}
                                                                            maxDate={new Date()}
                                                                            showYearDropdown
                                                                            dateFormatCalendar="MMMM"
                                                                            yearDropdownItemNumber={15}
                                                                            scrollableYearDropdown
                                                                            autoComplete="off"
                                                                        />
                                                                        {errors.hhMembers &&
                                                                            errors.hhMembers[index] &&
                                                                            errors.hhMembers[index].dob && (
                                                                                <div className="invalid-feedback d-block">
                                                                                    {errors.hhMembers[index].dob}
                                                                                </div>
                                                                            )}
                                                                        <Form.Text className="text-muted">
                                                                            {t('intake.dobHelper')}
                                                                        </Form.Text>
                                                                    </Form.Group>
                                                                )}

                                                                {enabledFields.includes('dateOfBirth') && (
                                                                    <Form.Group controlId={`hhMembers.${index}.hhmEstimatedDOB`}>
                                                                        <Form.Check
                                                                            type="checkbox"
                                                                            name={`hhMembers.${index}.hhmEstimatedDOB`}
                                                                            label={t('intake.dobEstimated')}
                                                                            checked={member.hhmEstimatedDOB || false}
                                                                            onChange={handleChange}
                                                                        />
                                                                    </Form.Group>
                                                                )}

                                                                {enabledFields.includes('age') && (
                                                                    <Form.Group controlId={`hhMembers.${index}.age`}>
                                                                        <LabelField
                                                                            label={questionPhrasing.age || t('personal.age')}
                                                                            isRequired={true}
                                                                        />
                                                                        <Form.Control
                                                                            type="text"
                                                                            name={`hhMembers.${index}.age`}
                                                                            placeholder="Age"
                                                                            onChange={handleChange}
                                                                            value={member.age || ''}
                                                                            isInvalid={
                                                                                errors.hhMembers &&
                                                                                errors.hhMembers[index] &&
                                                                                !!errors.hhMembers[index].age
                                                                            }
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">
                                                                            {errors.hhMembers &&
                                                                                errors.hhMembers[index] &&
                                                                                errors.hhMembers[index].age}
                                                                        </Form.Control.Feedback>
                                                                    </Form.Group>
                                                                )}

                                                                {enabledFields.includes('gender') && (
                                                                    <Form.Group controlId={`hhMembers.${index}.hhmGender`}>
                                                                        <LabelField
                                                                            label={questionPhrasing.gender || t('intake.gender')}
                                                                            isRequired={true}
                                                                        />
                                                                        <SelectField
                                                                            as="select"
                                                                            name={`hhMembers.${index}.hhmGender`}
                                                                            value={member.hhmGender || ''}
                                                                            isInvalid={
                                                                                errors.hhMembers &&
                                                                                errors.hhMembers[index] &&
                                                                                !!errors.hhMembers[index].hhmGender
                                                                            }
                                                                            options={dataTypes.genders}
                                                                            includeEmpty={true}
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">
                                                                            {errors.hhMembers &&
                                                                                errors.hhMembers[index] &&
                                                                                errors.hhMembers[index].hhmGender}
                                                                        </Form.Control.Feedback>
                                                                    </Form.Group>
                                                                )}

                                                                {enabledFields.includes('relationship') && (
                                                                    <Form.Group controlId={`hhMembers.${index}.hhmRelationship`}>
                                                                        <LabelField
                                                                            label={questionPhrasing.relationships || t('intake.relationships')}
                                                                            isRequired={true}
                                                                        />
                                                                        <SelectField
                                                                            as="select"
                                                                            name={`hhMembers.${index}.hhmRelationship`}
                                                                            value={member.hhmRelationship || ''}
                                                                            isInvalid={
                                                                                errors.hhMembers &&
                                                                                errors.hhMembers[index] &&
                                                                                !!errors.hhMembers[index].hhmRelationship
                                                                            }
                                                                            options={dataTypes.relationships}
                                                                            includeEmpty={true}
                                                                        />
                                                                        <Form.Control.Feedback type="invalid">
                                                                            {errors.hhMembers &&
                                                                                errors.hhMembers[index] &&
                                                                                errors.hhMembers[index].hhmRelationship}
                                                                        </Form.Control.Feedback>
                                                                    </Form.Group>
                                                                )}

                                                                <div className="text-right">
                                                                    <Button
                                                                        type="button"
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            arrayHelpers.remove(index);
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-trash-o fa-lg"></i>
                                                                    </Button>
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    );
                                                })}

                                                <Row className="mb-4">
                                                    <Col md={6} className="mb-2 mb-md-0">
                                                        <Button
                                                            type="button"
                                                            variant="outline-secondary"
                                                            className="w-100"
                                                            onClick={() => {
                                                                arrayHelpers.push({
                                                                    firstName: '',
                                                                    lastName: '',
                                                                    dob: '',
                                                                    hhmEstimatedDOB: false,
                                                                    hhmGender: '',
                                                                    hhmRelationship: '',
                                                                });
                                                            }}
                                                        >
                                                            {t('intake.addMember')}
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </>
                                        )}
                                    </>
                                )}
                            />

                            <Row>
                                <Col md={6} className="mb-2 mb-md-0">
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        className="w-100"
                                        onClick={() => onBack(values)}
                                    >
                                        {t('core.action.back')}
                                    </Button>
                                </Col>
                                <Col md={6}>
                                    <Button type="submit" variant="primary" className="w-100" disabled={isLoading}>
                                        {isLoading ? t('core.action.loading') : t('core.action.next')}
                                    </Button>
                                </Col>
                            </Row>
                            <ScrollToFirstError />
                        </Form>
                        {isEdit && (
                            <p className="text-center mt-3 mb-0">
                                <Link to="/dashboard">{t('dashboard.return')}</Link>
                            </p>
                        )}
                    </Col>
                );
            }}
        </Formik>
    );
};

export default ProfileFormStep2;